import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#232D4B] px-4 py-10 sm:px-6">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -start-24 -top-24 h-72 w-72 rounded-full bg-[#E57200]/10 blur-3xl" />
        <div className="absolute -bottom-32 -end-20 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
      </div>

      <div className="absolute end-4 top-4 z-20 sm:end-6 sm:top-6">
        <LanguageSwitcher />
      </div>

      <Card className="relative z-10 w-full max-w-md rounded-2xl border-white/20 bg-white shadow-2xl shadow-black/25" data-testid="login-card">
        <CardHeader className="space-y-3 px-6 pb-5 pt-8 text-center sm:px-10 sm:pt-10">
          <img
            src="/brand/logo-icon.png"
            alt="UniGate Solution"
            className="mx-auto h-20 w-auto object-contain sm:h-24"
          />
          <CardTitle className="text-3xl font-bold tracking-tight text-[#232D4B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            UniGate Solution
          </CardTitle>
          <CardDescription className="text-sm text-[#6B7280] sm:text-base" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Sign in to continue to UniGate Solution
          </CardDescription>
        </CardHeader>
        <CardContent className="px-6 pb-8 sm:px-10 sm:pb-10">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive" data-testid="error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {location.state?.message && (
              <Alert data-testid="success-alert">
                <AlertDescription>{location.state.message}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="font-medium text-[#232D4B]" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="h-11 rounded-lg border-gray-300 bg-white px-3 text-[#232D4B] transition-colors focus-visible:border-[#E57200] focus-visible:ring-2 focus-visible:ring-[#E57200]/25"
                style={{ fontFamily: 'Figtree, sans-serif' }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-medium text-[#232D4B]" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-11 rounded-lg border-gray-300 bg-white px-3 text-[#232D4B] transition-colors focus-visible:border-[#E57200] focus-visible:ring-2 focus-visible:ring-[#E57200]/25"
                style={{ fontFamily: 'Figtree, sans-serif' }}
              />
            </div>

            <Button
              type="submit"
              className="h-11 w-full rounded-lg bg-[#E57200] font-semibold text-white shadow-sm transition-colors hover:bg-[#CC6600] focus-visible:ring-2 focus-visible:ring-[#E57200] focus-visible:ring-offset-2 disabled:bg-[#E57200]/60"
              style={{ fontFamily: 'Figtree, sans-serif' }}
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Loading...' : t('login')}
            </Button>
          </form>

        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
